import { createSelector } from 'reselect';
import { RootState } from '../../../../types';
import { EntityTypes } from '../../../../constants/entities';
import { IEntityReducer } from '../../../reducers/entities';
import { IAction, IActionBasic, IActionDAO, IActionEntity, IActionEntityBasic } from 'types/domain/action';
import { IModelEntityConfig } from 'types/domain/model';
import { IMediaAsset } from 'types/domain/media';
import { ISceneConfig } from 'types/domain/scene';
import { IModuleOut } from 'types/domain/module';
import { getEntities } from 'core/selectors/utils/getEntities';
import { parseActionEntityBasic } from './actionEntity';
import { parseSceneDAO } from '../scene/scene';
import { IKeyArray } from 'types/IKeyArray';
import { IUploadFile } from 'core/slices/upload';
import { parseVariableDAO } from 'core/selectors/admin/variable';
import { createCachedSelector } from 're-reselect';

const entity = (state: RootState) => state.entity.present;
const id = (state: RootState, id?: number) => id || state.state[EntityTypes.ACTION];
const uploads = (state: RootState) => state.core.upload.uploads;

export const selectAction = createCachedSelector(
  entity,
  id,
  uploads,
  parseAction,
)((state, id) => id || state.state[EntityTypes.ACTION] || 'unknown');
export const selectActionDAO = createCachedSelector(
  entity,
  id,
  uploads,
  parseActionDAO,
)((state, id) => id || state.state[EntityTypes.ACTION] || 'unknown');

export function parseAction(entity: IEntityReducer, id: number, uploads?: IKeyArray<IUploadFile>) {
  if (id === null) return null;

  const action: IAction = Object.values(entity[EntityTypes.ACTION]).find(a => [a.id, a.offlineId].includes(id));

  if (!action) return null;

  const entities: IActionEntityBasic[] =
    Object.values(entity[EntityTypes.ACTION_ENTITY])
      .filter((ent: IActionEntity) => ent.actionId === id && ent.status > -1)
      .map(ent => parseActionEntityBasic(entity, ent.id)) || [];

  const scene = action.sceneId ? entity[EntityTypes.SCENE][action.sceneId] : null;

  return action
    ? ({
        ...action,
        sceneConfigId: action.sceneConfigId || scene?.sceneConfigId,
        entities,
        isDecision: entities.filter(e => e && e.correct).length === 1,
        isMultichoice: entities.filter(e => e && e.correct).length > 1,
        text: action.textId ? entity[EntityTypes.TEXT][action.textId] : null,
        node: action.nodeId ? entity[EntityTypes.NODE][action.nodeId] : null,
        scene,
        name: action.name || entity[EntityTypes.SCENE][action.sceneId]?.name,
        variable: action.variableId ? entity[EntityTypes.VARIABLE][action.variableId] : null,
        sceneConfig: action.sceneConfigId
          ? (entity[EntityTypes.SCENE_CONFIG][action.sceneConfigId] as ISceneConfig)
          : scene
          ? entity[EntityTypes.SCENE_CONFIG][scene.sceneConfigId]
          : null,
      } as IActionBasic)
    : null;
}

export function parseActionDAO(entity: IEntityReducer, id: number, uploads?: IKeyArray<IUploadFile>) {
  if (id === null) return null;

  const action: IAction = Object.values(entity[EntityTypes.ACTION]).find(a => [a.id, a.offlineId].includes(id));
  const entities: IActionEntityBasic[] =
    Object.values(entity[EntityTypes.ACTION_ENTITY])
      .filter((ent: IActionEntity) => ent.actionId === id && ent.status > -1)
      .map(ent => parseActionEntityBasic(entity, ent.id)) || [];

  return action
    ? ({
        ...action,
        text: action.textId ? entity[EntityTypes.TEXT][action.textId] : null,
        entities,
        isDecision: entities.filter(e => e && e.status > -1).length > 0,
        isMultichoice: entities.filter(e => e && e.correct).length >= 1,
        node: action.nodeId ? entity[EntityTypes.NODE][action.nodeId] : null,
        modelConfigs: Object.values(entity[EntityTypes.MODEL_ENTITY_CONFIG]).filter(
          (mec: IModelEntityConfig) => mec.actionId === id,
        ),

        scene: action.sceneId ? parseSceneDAO(entity, action.sceneId) : null,
        variable: action.variableId ? parseVariableDAO(entity, action.variableId) : null,
        mediaAsset: action.mediaAssetId ? (entity[EntityTypes.MEDIA_ASSET][action.mediaAssetId] as IMediaAsset) : null,
        sceneConfig: action.sceneConfigId
          ? (entity[EntityTypes.SCENE_CONFIG][action.sceneConfigId] as ISceneConfig)
          : null,
        outs: getEntities(entity, EntityTypes.MODULE_OUT, 'sourceId', id)
          .filter(
            (out: IModuleOut) =>
              entity[EntityTypes.ACTION][out.targetId] && entity[EntityTypes.ACTION][out.targetId].status > -1,
          )
          .reduce((prev, next: IModuleOut) => {
            return { ...prev, [`${next.sourceHandle}`]: next };
          }, {}),
        upload: uploads
          ? Object.values(uploads).find((up: IUploadFile) => up.parentType === EntityTypes.ACTION && up.parentId === id)
          : null,
      } as IActionDAO)
    : null;
}

export const selectActionType = createSelector([entity, id], (entity, id) => entity[EntityTypes.ACTION][id]?.type);
